import type { User } from "@shared/models/auth";
import type { CategoryKey } from "@shared/schema";
import { STORE_FOOD_CATEGORY, STORE_SERVICE_CATEGORY } from "@shared/ownProductCategories";

/** Normalize API/session booleans (Postgres driver or proxies may send 1 / "true"). */
export function asBool(v: unknown): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") {
    const n = v.trim().toLowerCase();
    return n === "1" || n === "true" || n === "t" || n === "yes" || n === "y" || n === "on";
  }
  return false;
}

export function normalizeUserBooleans(user: User): User {
  return {
    ...user,
    isVirtualStore: asBool(user.isVirtualStore) as User["isVirtualStore"],
    hasPhysicalStore: asBool(user.hasPhysicalStore) as User["hasPhysicalStore"],
    isPremiumAccount: asBool(user.isPremiumAccount) as User["isPremiumAccount"],
    isSuspended: asBool(user.isSuspended) as User["isSuspended"],
    isFrozen: asBool(user.isFrozen) as User["isFrozen"],
    isRetailApproved: asBool(user.isRetailApproved) as User["isRetailApproved"],
    isAdmin: asBool(user.isAdmin) as User["isAdmin"],
    isFeatured: asBool(user.isFeatured) as User["isFeatured"],
  };
}

/** Commercial store role. Physical presence is only a store attribute, not a separate role. */
export function isStoreOwnerRole(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.accountType === "wholesaler") return false;
  return (
    asBool(user.isVirtualStore) ||
    user.accountType === "reseller" ||
    !!user.virtualStoreName
  );
}

/** Real-world storefront marker on top of an existing store role. */
export function hasPhysicalStorefront(user: User | null | undefined): boolean {
  return isStoreOwnerRole(user) && asBool(user?.hasPhysicalStore);
}

/** Wholesaler account without an activated store role — UI should show "تاجر جملة" only here. */
export function isWholesaleMerchantOnly(user: User | null | undefined): boolean {
  return !!user && user.accountType === "wholesaler";
}

/** Match backend wholesale-visibility access rules exactly. */
export function canSeeWholesaleRole(user: User | null | undefined): boolean {
  if (!user) return false;
  return asBool(user.isVirtualStore) || user.accountType === "wholesaler" || user.accountType === "reseller";
}

export function isBuyerOnlyRole(user: User | null | undefined): boolean {
  return !!user && !isWholesaleMerchantOnly(user) && !isStoreOwnerRole(user);
}

/**
 * «منتجات اليوم» destination. Stores and approved merchants open the
 * wholesale-dropshipping view of today's products; everyone else the public
 * one. Shared by the Header nav and the Home feature card so both stay on the
 * rule Home has always used.
 */
export function getTodayProductsHref(user: User | null | undefined): string {
  return isStoreOwnerRole(user) || user?.isRetailApproved
    ? "/today?merchantDropshippingToday=1"
    : "/today";
}

/**
 * 3-tier account policy mirror: only customers may add to cart and place
 * orders. Wholesalers (never buy) and stores (buy from wholesalers via
 * a different channel, not the cart) must not see the cart UI.
 *
 * Backed by server-side guards in server/lib/accountPolicy.ts — this is
 * only a UX hint; any bypass is still rejected with 403.
 */
export function canBuyAsCustomer(user: User | null | undefined): boolean {
  if (!user) return true; // anonymous visitor — keep cart visible (login wall handles checkout)
  return !isWholesaleMerchantOnly(user) && !isStoreOwnerRole(user);
}

export const WHOLESALER_ALLOWED_CATEGORY_KEYS: CategoryKey[] = [
  "alisveris",
  "riyada",
  "atfal",
  "tibbi",
  "yedek-parca",
];

/**
 * Browsing helper — returns ALL category keys for every user role, including
 * wholesale merchants.  Use this for Home, category pages, search filters,
 * market pages, and anywhere a user navigates or browses listings.
 */
export function getVisibleCategoryKeysForUser(
  _user: User | null | undefined,
  categoryKeys: CategoryKey[],
): CategoryKey[] {
  return categoryKeys;
}

/**
 * Publishing helper — restricts commercial accounts (wholesale merchants AND
 * store owners, whether virtual or physical) to the cart-eligible categories
 * they are allowed to publish. Stores and merchants sell goods that go through
 * the cart/checkout, so they may only post in those categories. Ordinary
 * registered customers keep the full category list — they post classified ads
 * for single / used items in any category.
 *
 * Use this ONLY in create-listing / edit-listing / own-product flows, never in
 * browsing contexts.
 */
export function getPublishableCategoryKeysForUser(
  user: User | null | undefined,
  categoryKeys: CategoryKey[],
): CategoryKey[] {
  return (isWholesaleMerchantOnly(user) || isStoreOwnerRole(user))
    ? categoryKeys.filter((key) => WHOLESALER_ALLOWED_CATEGORY_KEYS.includes(key))
    : categoryKeys;
}

/**
 * Categories a store owner may file an OWN product under: the publishable
 * cart categories above, plus — for a RETAIL store only — «الأغذية» (cart
 * goods, issue #176, owner decision 2026-10-24) and «الخدمات» (contact only,
 * owner decision 2026-10-20). WHOLESALER_ALLOWED_CATEGORY_KEYS /
 * getPublishableCategoryKeysForUser stay unchanged, so merchants and regular
 * listings never gain food or services. The server enforces the same rule
 * (getAccountRole === "store").
 */
export function getOwnProductCategoryKeysForUser(
  user: User | null | undefined,
  categoryKeys: CategoryKey[],
): CategoryKey[] {
  const base = getPublishableCategoryKeysForUser(user, categoryKeys);
  if (!isStoreOwnerRole(user)) return base;
  const storeOnly = [STORE_FOOD_CATEGORY, STORE_SERVICE_CATEGORY] as CategoryKey[];
  return [...base, ...storeOnly.filter((key) => categoryKeys.includes(key) && !base.includes(key))];
}
